// App.tsx - メインアプリケーション構造

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { ConfigProvider, theme } from 'antd';
import jaJP from 'antd/lib/locale/ja_JP';

// 設定ファイルからベースパスをインポート
import { APP_BASE_PATH } from './config';

// Flutter検出ユーティリティをインポート
import { setFlutterState, debugFlutterDetection } from './utils/flutterDetection';

// グローバルCSS
import './styles/global.css';

// 各画面のインポート
import LoginPage from './pages/LoginPage';
import MenuPage from './pages/MenuPage';
import CameraPage from './pages/CameraPage';
import ImageUploadPage from './pages/ImageUploadPage';
import EditResultPage from './pages/EditResultPage';
import AdditionalInfoPage from './pages/AdditionalInfoPage';
import FollowRegistrationPage from './pages/FollowRegistrationPage';
import BusinessCardListPage from './pages/BusinessCardListPage';
import BusinessCardDetailPage from './pages/BusinessCardDetailPage';

// 認証コンテキスト
import { AuthProvider, useAuth } from './contexts/AuthContext';


// カスタムテーマ設定
const customTheme = {
  token: {
    colorPrimary: '#0693e3',
    controlHeight: 50,      // ボタンなどのコントロールの基本高さを大きく
    fontSize: 16,           // フォントサイズを少し大きく
    paddingXS: 12,         // 極小パディング
    paddingSM: 16,         // 小パディング
    padding: 20,           // 標準パディング
    paddingMD: 24,         // 中パディング
    paddingLG: 28,         // 大パディング
    paddingXL: 32,         // 特大パディング
  },
  components: {
    Button: {
      controlHeight: 50,    // ボタンの高さを直接指定
      minHeight: 50,        // 最小高さも指定
      paddingInline: 20,    // 左右のパディング
      fontSize: 16,         // ボタン内の文字サイズ
      borderRadius: 6,      // 角丸の半径
    }
  }
};

// 認証が必要なルートのラッパー
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, hasSelectedCompany } = useAuth();
  
  // 未認証の場合はログイン画面へリダイレクト
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // 認証済みだが企業選択がまだの場合もログイン画面へ（企業選択ステップが表示される）
  if (!hasSelectedCompany) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// ルートパス "/" の遷移先を認証状態で決める。
// 旧: "/" は無条件で /login へ飛ばしていたため、Flutter が毎起動 /login を検知して
// 注入＋フルリロードを行い「二重ロード」を招いていた（起動ちらつき・APIバーストの一因）。
// 新: 有効セッションがあれば /menu へ、無ければ /login へ SPA 遷移する（フルリロードなし）。
// AuthProvider は復元完了(!loading)後にのみ children を描画するため、ここでは
// isAuthenticated / hasSelectedCompany は確定済み。
// ※未認証（＝旧Flutterで注入前の状態を含む）なら従来どおり /login へ落ちるので、
//   旧Flutterの /login 検知→注入→リロードのフォールバック経路はそのまま機能する。
const RootRedirect: React.FC = () => {
  const { isAuthenticated, hasSelectedCompany } = useAuth();
  if (isAuthenticated && hasSelectedCompany) {
    return <Navigate to="/menu" replace />;
  }
  return <Navigate to="/login" replace />;
};

// Flutter のフッター等から「フルリロードしないクライアント遷移」を行えるよう、
// グローバル関数 window.__flutterNavigate を公開する。
// フルリロード(loadRequest)だと React を毎回作り直してブート中に白くなるため、
// Flutter 側はこの関数を優先的に呼ぶ（存在しなければ従来の loadRequest にフォールバック）。
// useNavigate を使うので必ず <Router> の内側に置くこと。
const FlutterNavBridge: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    (window as any).__flutterNavigate = (path: string) => {
      try {
        navigate(path);
      } catch {
        // 失敗時はフルリロードにフォールバック
        try { window.location.assign(path); } catch { /* no-op */ }
      }
    };
    return () => {
      try { delete (window as any).__flutterNavigate; } catch { /* no-op */ }
    };
  }, [navigate]);
  return null;
};

const App: React.FC = () => {
  // アプリケーション初期化時にFlutter状態を設定
  useEffect(() => {
    // Flutter環境を検出して永続化
    setFlutterState();

    // デバッグ情報を出力（開発時のみ）
    if (process.env.NODE_ENV === 'development') {
      console.log('App initialized, Flutter detection:');
      debugFlutterDetection();
    }
  }, []);

  // Flutterフッター高さをCSS変数としてグローバルに管理
  useEffect(() => {
    const apply = (height: number) => {
      if (height > 0) {
        document.documentElement.style.setProperty('--flutter-footer-height', `${height}px`);
        document.body.classList.add('flutter-footer-visible');
      } else {
        document.documentElement.style.removeProperty('--flutter-footer-height');
        document.body.classList.remove('flutter-footer-visible');
      }
    };

    const handleChange = (event: any) => apply(event.detail?.footerHeight || 0);
    window.addEventListener('flutterFooterStateChange', handleChange);

    const initial = (window as any).flutterFooterState;
    if (initial?.footerHeight) apply(initial.footerHeight);

    return () => window.removeEventListener('flutterFooterStateChange', handleChange);
  }, []);

  return (
    <ConfigProvider
      locale={jaJP}
      theme={customTheme}
    >
      <AuthProvider>
        <Router basename={APP_BASE_PATH === '/' ? '' : APP_BASE_PATH.endsWith('/') ? APP_BASE_PATH.slice(0, -1) : APP_BASE_PATH}>
          <FlutterNavBridge />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/menu" element={
              <ProtectedRoute>
                <MenuPage />
              </ProtectedRoute>
            } />
            <Route path="/camera" element={
              <ProtectedRoute>
                <CameraPage />
              </ProtectedRoute>
            } />
            <Route path="/image-upload" element={
              <ProtectedRoute>
                <ImageUploadPage />
              </ProtectedRoute>
            } />
            <Route path="/edit" element={
              <ProtectedRoute>
                <EditResultPage />
              </ProtectedRoute>
            } />
            <Route path="/additional-info" element={
              <ProtectedRoute>
                <AdditionalInfoPage />
              </ProtectedRoute>
            } />
            <Route path="/follow-registration" element={
              <ProtectedRoute>
                <FollowRegistrationPage />
              </ProtectedRoute>
            } />
            <Route path="/business-card-list" element={
              <ProtectedRoute>
                <BusinessCardListPage />
              </ProtectedRoute>
            } />
            <Route path="/business-card-detail" element={
              <ProtectedRoute>
                <BusinessCardDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;